import { useEffect, useRef, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { drawItems } from '../../../utils/markupRender';
import { contentOf, useContentRect } from './hooks/useContentRect';
import { TextBoxEditor } from './TextBoxEditor';
import type { TextBoxState } from '../hooks/useTextBox';
import { hitTestItem, itemRectPx, topmostHitIndex, type BoxItem } from '../../../utils/markupHitTest';
import {
  drawMarquee, drawSelectionFrame, ERASE_TOLERANCE_PX, hitsResizeHandle,
  MIN_STROKE_RESIZE_PCT, pointInsideSelectionFrame, rectsIntersect,
  SELECT_TOLERANCE_PX, strokeBoundsPct, topmostBoxHit,
} from './selectionGeometry';
import {
  DEFAULT_TEXT_BOX_WIDTH,
  isFreehandTool,
  itemAnchor,
  MIN_TEXT_BOX_WIDTH,
  resizeItemToWidth,
  resolveMarkupColor,
  translateItem,
  type IMarkupPoint,
  type ITextStyle,
  type MarkupColor,
  type MarkupItem,
  type MarkupTool,
} from '../../../utils/markupTypes';
import styles from './DrawingLayer.module.css';

interface IDrawingLayerProps {
  /** The rendered content (an `<img>` or pdf `<canvas>`) as children. */
  children: ReactNode;
  /** Re-measures when this changes — a PDF page flip resizes the same canvas. */
  contentKey: string | number;
  items: readonly MarkupItem[];
  /** Marks from OTHER people's saved annotations, painted underneath and never
   *  touched: they are not in `items`, so no tool, eraser or selection can
   *  reach them (docs/adr/0013). Absent in the modal, which has no backdrop. */
  backdropItems?: readonly MarkupItem[];
  tool: MarkupTool;
  color: MarkupColor;
  /** The next stroke's line width, as a fraction of content width. */
  strokeWidth: number;
  textStyle: ITextStyle;
  /** The selected items' indices — each drawn with a dashed frame. A marquee
   *  drag over empty space can select several at once. */
  selectedIndices: readonly number[];
  disabled: boolean;
  onCommit: (item: MarkupItem) => void;
  /** Erase the item at this index of `items` (the eraser tool's target). */
  onErase: (index: number) => void;
  /** Selection changed ([] = clicked empty space / marquee caught nothing). */
  onSelect: (indices: number[]) => void;
  /** A selected item was dragged to a new anchor (strokes translate). */
  onMoveItem: (index: number, to: { x: number; y: number }) => void;
  /** A multi-item selection was dragged together — one undo step for the group. */
  onMoveItems: (moves: { index: number; to: { x: number; y: number } }[]) => void;
  /** The selected item's width handle was dragged — boxes change width,
   *  strokes scale (h set for aspect-locked images). */
  onResizeItem: (index: number, w: number) => void;
  /** The open text box and its words — owned by the drawing model, so a save sees them. */
  textBox: TextBoxState;
  /** Turns the typed words into a stamp and closes the box. */
  onSettleText: () => void;
  textInputLabel: string;
}

/**
 * The drawing surface: a canvas overlay sized to the content element's own
 * rect (not the letterboxed stage), kept in sync by a ResizeObserver. All
 * geometry is captured as percentages of that rect, so strokes stay anchored
 * to the same content feature at any display size — and at export size.
 *
 * In-progress drawing (the stroke being dragged) lives here; only completed
 * items are reported up via `onCommit`. The open text box lives in the model.
 */
export function DrawingLayer({
  children, contentKey, items, backdropItems, tool, color, strokeWidth, textStyle, selectedIndices, disabled,
  onCommit, onErase, onSelect, onMoveItem, onMoveItems, onResizeItem, textBox, onSettleText, textInputLabel,
}: IDrawingLayerProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const contentRect = useContentRect(stageRef, contentKey);
  // Single-item operations (resize handle, text-tool drag/select) only make
  // sense when exactly one item is selected.
  const selectedIndex = selectedIndices.length === 1 ? selectedIndices[0]! : null;

  // The stroke being drawn — in a ref so pointermove never re-renders React;
  // the overlay repaints imperatively. Mirrored into state only on commit.
  const strokeInProgressRef = useRef<MarkupItem | null>(null);
  
  // True while the eraser is held down — dragging keeps erasing what it crosses.
  const isErasingRef = useRef(false);
  // The list the last erase was aimed at. Until the page re-renders without that
  // mark, positions in `items` are stale, so the next erase waits for it.
  const erasedFromRef = useRef<readonly MarkupItem[] | null>(null);
  
  // An in-flight drag of a selected item; `pos` (the item's would-be anchor)
  // previews on the overlay until pointerup commits the move through the
  // owner (for undo).
  const itemDragRef = useRef<{ index: number; dx: number; dy: number; moved: boolean; pos: IMarkupPoint } | null>(null);
  
  // An in-flight width-resize of the selected box, previewed like a drag.
  const boxResizeRef = useRef<{ index: number; w: number; moved: boolean } | null>(null);

  // A select-tool drag started on empty space: the marquee rect (in %) that
  // picks up every item it overlaps on release.
  const marqueeRef = useRef<{ start: IMarkupPoint; end: IMarkupPoint; moved: boolean } | null>(null);

  // A drag of the whole current multi-selection, all items offset by the
  // same delta from the pointer's travel since pointerdown.
  const groupDragRef = useRef<{
    indices: number[];
    anchors: Map<number, IMarkupPoint>;
    start: IMarkupPoint;
    offset: { dx: number; dy: number };
    moved: boolean;
  } | null>(null);

  // The floating textarea: a new stamp being written, or (editIndex set) an
  // existing stamp's content being rewritten in place.
  const openTextBox = textBox.box;

  const repaint = () => {
    const overlay = overlayCanvasRef.current;
    if (!overlay || !contentRect) return;
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, contentRect.width, contentRect.height);

    // What is already saved on this version, under the drawing in progress.
    if (backdropItems && backdropItems.length > 0) {
      drawItems(ctx, backdropItems, contentRect.width, contentRect.height, resolveMarkupColor);
    }

    // A dragged/resized item (or a dragged group) previews at its in-flight
    // geometry until commit.
    const drag = itemDragRef.current;
    const resize = boxResizeRef.current;
    const groupDrag = groupDragRef.current;
    const displayItems = items.map((it, i) => {
      if (drag && i === drag.index) return translateItem(it, drag.pos);
      if (resize && i === resize.index) return resizeItemToWidth(it, resize.w);
      if (groupDrag?.anchors.has(i)) {
        const anchor = groupDrag.anchors.get(i)!;
        return translateItem(it, { x: anchor.x + groupDrag.offset.dx, y: anchor.y + groupDrag.offset.dy });
      }
      return it;
    });
    // A stamp being content-edited is replaced visually by the textarea.
    const editIndex = openTextBox?.editIndex ?? null;
    const toDraw = displayItems.filter((_, i) => i !== editIndex);
    if (strokeInProgressRef.current) toDraw.push(strokeInProgressRef.current);
    drawItems(ctx, toDraw, contentRect.width, contentRect.height, resolveMarkupColor);

    for (const i of selectedIndices) {
      if (i === editIndex) continue;
      const item = displayItems[i];
      if (item) drawSelectionFrame(ctx, item, contentRect.width, contentRect.height, selectedIndices.length === 1);
    }

    const marquee = marqueeRef.current;
    if (marquee) {
      const toPx = (p: IMarkupPoint) => ({ x: (p.x / 100) * contentRect.width, y: (p.y / 100) * contentRect.height });
      const a = toPx(marquee.start);
      const b = toPx(marquee.end);
      drawMarquee(ctx, {
        left: Math.min(a.x, b.x),
        top: Math.min(a.y, b.y),
        w: Math.abs(b.x - a.x),
        h: Math.abs(b.y - a.y),
      });
    }
  };

  // Committed items (and geometry/selection changes) repaint declaratively;
  // pointer moves repaint imperatively through the same function.
  useEffect(repaint, [items, backdropItems, contentRect, selectedIndices, openTextBox]);

  function pointOf(e: { clientX: number; clientY: number }): IMarkupPoint | null {
    const content = contentOf(stageRef.current);
    if (!content) return null;
    const rect = content.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return null;
    return { x, y };
  }

  function handlePointerDown(e: PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    if (openTextBox) {
      // A click outside the open text input settles it: commit what was
      // typed, or close it when empty. Never start a drawing from that click.
      onSettleText();
      return;
    }
    const p = pointOf(e);
    if (!p) return;

    if (tool === 'select') {
      if (!contentRect) return;
      // The selected item's resize handle takes priority, as in text mode —
      // only offered for a solo selection.
      const selected = selectedIndex !== null ? items[selectedIndex] : undefined;
      if (selected && hitsResizeHandle(selected, p, contentRect)) {
        const w = selected.kind === 'stroke' ? strokeBoundsPct(selected.points).w : selected.w;
        boxResizeRef.current = { index: selectedIndex!, w, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }
      // A click anywhere inside an already-selected item's dashed frame
      // grabs it — not just its thin outline, which is all a hollow
      // rect/ellipse/cloud would otherwise hit-test against.
      if (selectedIndices.some((i) => items[i] && pointInsideSelectionFrame(items[i]!, p, contentRect))) {
        if (selectedIndices.length > 1) {
          // Grabbing any member of a multi-selection drags the whole group
          // together, each item keeping its offset from the pointer.
          const anchors = new Map(selectedIndices.map((i) => [i, itemAnchor(items[i]!)]));
          groupDragRef.current = { indices: [...selectedIndices], anchors, start: p, offset: { dx: 0, dy: 0 }, moved: false };
        } else {
          const anchor = itemAnchor(items[selectedIndex!]!);
          itemDragRef.current = { index: selectedIndex!, dx: p.x - anchor.x, dy: p.y - anchor.y, moved: false, pos: anchor };
        }
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }
      // Otherwise, any item is grabbable via a precise hit — strokes included.
      const hit = topmostHitIndex(items, p, contentRect.width, contentRect.height, SELECT_TOLERANCE_PX);
      if (hit === null) {
        // Empty space: a marquee drag picks up whatever it overlaps on
        // release; a plain click (no drag) deselects, handled in pointerup.
        marqueeRef.current = { start: p, end: p, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }
      onSelect([hit]);
      const anchor = itemAnchor(items[hit]!);
      itemDragRef.current = { index: hit, dx: p.x - anchor.x, dy: p.y - anchor.y, moved: false, pos: anchor };
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }

    if (tool === 'text') {
      if (!contentRect) return;
      // The selected box's resize handle takes priority over everything.
      const selected = selectedIndex !== null ? items[selectedIndex] : undefined;
      if (selected && selected.kind === 'text' && hitsResizeHandle(selected, p, contentRect)) {
        boxResizeRef.current = { index: selectedIndex!, w: selected.w, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }
      // A click on an existing box selects it (and arms a drag); a click on
      // empty space deselects first, then a further click places a new box.
      const hit = topmostBoxHit(items, p, contentRect);
      if (hit !== null) {
        onSelect([hit]);
        const it = items[hit] as BoxItem;
        itemDragRef.current = { index: hit, dx: p.x - it.x, dy: p.y - it.y, moved: false, pos: { x: it.x, y: it.y } };
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }
      if (selectedIndex !== null) {
        onSelect([]);
        return;
      }
      textBox.open({ ...p, w: Math.max(MIN_TEXT_BOX_WIDTH, Math.min(DEFAULT_TEXT_BOX_WIDTH, 100 - p.x)) });
      return;
    }

    if (tool === 'eraser') {
      e.currentTarget.setPointerCapture(e.pointerId);
      isErasingRef.current = true;
      eraseUnder(p);
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);
    strokeInProgressRef.current = {
      kind: 'stroke',
      tool,
      color,
      width: strokeWidth,
      points: isFreehandTool(tool) ? [p] : [p, p],
    };
    repaint();
  }

  /** Right-click on a text stamp reopens it for editing, in any tool. */
  function handleContextMenu(e: MouseEvent<HTMLCanvasElement>) {
    e.preventDefault(); // never the browser menu over the drawing surface
    if (disabled || openTextBox || !contentRect) return;
    const p = pointOf(e);
    if (!p) return;
    for (let i = items.length - 1; i >= 0; i -= 1) {
      const item = items[i]!;
      if (item.kind === 'text' && hitTestItem(item, p, contentRect.width, contentRect.height, SELECT_TOLERANCE_PX)) {
        onSelect([i]); // the format controls point at it while it's being edited
        textBox.open({ x: item.x, y: item.y, w: item.w, editIndex: i }, item.text);
        return;
      }
    }
  }

  /** Removes the topmost item under the point, if any. */
  function eraseUnder(p: IMarkupPoint) {
    if (!contentRect || erasedFromRef.current === items) return;
    const hit = topmostHitIndex(items, p, contentRect.width, contentRect.height, ERASE_TOLERANCE_PX);
    if (hit === null) return;
    erasedFromRef.current = items;
    onErase(hit);
  }

  function handlePointerMove(e: PointerEvent<HTMLCanvasElement>) {
    const resize = boxResizeRef.current;
    if (resize) {
      const p = pointOf(e);
      if (!p) return;
      const item = items[resize.index];
      if (!item) return;
      const left = item.kind === 'stroke' ? strokeBoundsPct(item.points).left : item.x;
      const minW = item.kind === 'stroke' ? MIN_STROKE_RESIZE_PCT : MIN_TEXT_BOX_WIDTH;
      const w = Math.max(minW, Math.min(100 - left, p.x - left));
      if (Math.abs(w - resize.w) > 0.05) resize.moved = true;
      resize.w = w;
      repaint();
      return;
    }
    const drag = itemDragRef.current;
    if (drag) {
      const p = pointOf(e);
      if (!p) return;
      const pos = {
        x: Math.min(100, Math.max(0, p.x - drag.dx)),
        y: Math.min(100, Math.max(0, p.y - drag.dy)),
      };
      if (Math.abs(pos.x - drag.pos.x) > 0.05 || Math.abs(pos.y - drag.pos.y) > 0.05) drag.moved = true;
      drag.pos = pos;
      repaint();
      return;
    }
    const groupDrag = groupDragRef.current;
    if (groupDrag) {
      const p = pointOf(e);
      if (!p) return;
      let dx = p.x - groupDrag.start.x;
      let dy = p.y - groupDrag.start.y;
      // Clamp so every item in the group stays on the page — the tightest
      // member's edge sets the limit for the whole group.
      for (const anchor of groupDrag.anchors.values()) {
        dx = Math.min(100 - anchor.x, Math.max(-anchor.x, dx));
        dy = Math.min(100 - anchor.y, Math.max(-anchor.y, dy));
      }
      if (Math.abs(dx - groupDrag.offset.dx) > 0.05 || Math.abs(dy - groupDrag.offset.dy) > 0.05) groupDrag.moved = true;
      groupDrag.offset = { dx, dy };
      repaint();
      return;
    }
    const marquee = marqueeRef.current;
    if (marquee) {
      const p = pointOf(e);
      if (!p) return;
      if (Math.abs(p.x - marquee.start.x) > 0.3 || Math.abs(p.y - marquee.start.y) > 0.3) marquee.moved = true;
      marquee.end = p;
      repaint();
      return;
    }
    if (isErasingRef.current) {
      const p = pointOf(e);
      if (p) eraseUnder(p);
      return;
    }
    const stroke = strokeInProgressRef.current;
    if (!stroke || stroke.kind !== 'stroke') return;
    const p = pointOf(e);
    if (!p) return;
    if (isFreehandTool(stroke.tool)) {
      // Skip sub-0.2% jitter so freehand polylines stay compact.
      const last = stroke.points[stroke.points.length - 1]!;
      if (Math.abs(p.x - last.x) < 0.2 && Math.abs(p.y - last.y) < 0.2) return;
      stroke.points.push(p);
    } else {
      stroke.points[1] = p;
    }
    repaint();
  }

  function handlePointerUp() {
    const resize = boxResizeRef.current;
    if (resize) {
      boxResizeRef.current = null;
      if (resize.moved) onResizeItem(resize.index, resize.w); // owner records it for undo
      else repaint();
      return;
    }
    const drag = itemDragRef.current;
    if (drag) {
      itemDragRef.current = null;
      if (drag.moved) onMoveItem(drag.index, drag.pos); // owner records it for undo
      else repaint(); // plain select-click — clear the drag preview
      return;
    }
    const groupDrag = groupDragRef.current;
    if (groupDrag) {
      groupDragRef.current = null;
      if (groupDrag.moved) {
        const moves = groupDrag.indices.map((index) => {
          const anchor = groupDrag.anchors.get(index)!;
          return { index, to: { x: anchor.x + groupDrag.offset.dx, y: anchor.y + groupDrag.offset.dy } };
        });
        onMoveItems(moves); // owner records it as one undo step
      } else repaint(); // plain click on a selected member — clear the drag preview
      return;
    }
    const marquee = marqueeRef.current;
    if (marquee) {
      marqueeRef.current = null;
      if (marquee.moved && contentRect) {
        const toPx = (p: IMarkupPoint) => ({ x: (p.x / 100) * contentRect.width, y: (p.y / 100) * contentRect.height });
        const a = toPx(marquee.start);
        const b = toPx(marquee.end);
        const rect = {
          left: Math.min(a.x, b.x),
          top: Math.min(a.y, b.y),
          w: Math.abs(b.x - a.x),
          h: Math.abs(b.y - a.y),
        };
        const indices: number[] = [];
        items.forEach((it, i) => {
          if (rectsIntersect(rect, itemRectPx(it, contentRect.width, contentRect.height))) indices.push(i);
        });
        onSelect(indices);
      } else {
        onSelect([]); // a plain click on empty space deselects
      }
      return;
    }
    isErasingRef.current = false;
    const stroke = strokeInProgressRef.current;
    strokeInProgressRef.current = null;
    if (!stroke || stroke.kind !== 'stroke') return;
    const [a, b] = [stroke.points[0]!, stroke.points[stroke.points.length - 1]!];
    const moved = Math.abs(b.x - a.x) > 0.2 || Math.abs(b.y - a.y) > 0.2 || stroke.points.length > 2;
    if (moved) onCommit(stroke);
    else repaint(); // a no-drag click draws nothing — clear the preview
  }

  return (
    <div ref={stageRef} className={styles.stage}>
      {children}
      {contentRect && (
        <div
          className={styles.overlayBox}
          style={{ left: contentRect.left, top: contentRect.top, width: contentRect.width, height: contentRect.height }}
        >
          <canvas
            ref={overlayCanvasRef}
            data-overlay
            className={`${styles.overlayCanvas} ${disabled ? '' : tool === 'select' ? styles.overlaySelect : styles.overlayActive}`}
            width={Math.round(contentRect.width * (window.devicePixelRatio || 1))}
            height={Math.round(contentRect.height * (window.devicePixelRatio || 1))}
            style={{ width: contentRect.width, height: contentRect.height }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onContextMenu={handleContextMenu}
          />
          {openTextBox && (
            <TextBoxEditor
              box={openTextBox}
              value={textBox.value}
              contentWidthPx={contentRect.width}
              color={color}
              textStyle={textStyle}
              inputLabel={textInputLabel}
              onChange={textBox.setValue}
              onCommit={onSettleText}
              onCancel={textBox.close}
            />
          )}
        </div>
      )}
    </div>
  );
}